import { useEffect } from "react";
import PageContainer from "@/pages/containers/page-container";
import { useIDCardQuery } from "@/hooks/use-id-card";
import IDCardSkeletonLoader from "@/pages/components/id-card/id-card-skeleton-loader";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import PDFViewer from "./components/base64-pdf-viewer/base64-pdf-viewer";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useFeatureName } from "@/hooks/use-pageTitle";
import { updateRequestedAt } from "@/store/request-id-card/requestId-slice";

const IDCard = () => {
  const featureName = useFeatureName();
  const dispatch = useDispatch();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const { requestedAt } = useSelector((state: RootState) => state.requestId);

  const {
    data: idCardData,
    isFetching: isIDCardFetching,
    isError: isIDCardError,
    error,
    isSuccess: isIdCardSuccess,
  } = useIDCardQuery(activeCoverage);
  useEffect(() => {
    if (isIdCardSuccess) {
      dispatch(
        updateRequestedAt({
          requestedAt: requestedAt || idCardData?.idCardRequestedAt,
          isLoaded: true,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdCardSuccess]);

  function renderIDCardContent() {
    if (isIDCardFetching) {
      return <IDCardSkeletonLoader />;
    }

    if (isIDCardError) {
      return <SomethingWentWrong message={error.message} />;
    }

    if (idCardData?.base64Encoded) {
      return <PDFViewer base64Encoded={idCardData.base64Encoded} />;
    }

    return (
      <div className="flex flex-1 items-center justify-center">
        <NoDataAvailable
          title="Unable to Render ID Card"
          message={idCardData?.message ?? "No Data Available"}
        />
      </div>
    );
  }

  return (
    <PageContainer title={featureName}>
      <div className="flex h-full items-center justify-center bg-slate-50 p-4">
        {renderIDCardContent()}
      </div>
    </PageContainer>
  );
};

export default IDCard;
